(() => {
  const all = (root, selector) => Array.from(root.querySelectorAll(selector));

  function installStableCartToggle() {
    if (document.documentElement.dataset.k2cpCartToggleReady === 'true') return;
    document.documentElement.dataset.k2cpCartToggleReady = 'true';

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-drawer-toggle="drawer-cart"]');
      if (!trigger) return;

      const drawer = document.querySelector('[data-drawer="drawer-cart"]');
      if (!drawer) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      drawer.dispatchEvent(new CustomEvent('theme:drawer:toggle', {
        bubbles: false
      }));
    }, true);
  }

  async function openCartDrawer(item) {
    const rootUrl = window.theme?.routes?.root_url || window.Shopify?.routes?.root || '/';
    const drawer = document.querySelector('[data-drawer="drawer-cart"]');

    if (!drawer) {
      throw new Error('The cart drawer is temporarily unavailable. Please try again.');
    }

    const readSection = async (name) => {
      const bundled = item?.sections?.[name];
      if (typeof bundled === 'string' && bundled.trim()) return bundled;
      const response = await fetch(`${rootUrl}?section_id=${encodeURIComponent(name)}`, {
        headers: { Accept: 'text/html' }
      });
      if (!response.ok) throw new Error(`Unable to refresh ${name}.`);
      return response.text();
    };

    const [cartResponse, itemsHTML, subtotalHTML] = await Promise.all([
      fetch(`${window.theme?.routes?.cart || `${rootUrl}cart`}.js`, { headers: { Accept: 'application/json' } }),
      readSection('api-cart-items'),
      readSection('api-cart-subtotal')
    ]);

    if (!cartResponse.ok) throw new Error('Unable to refresh cart data.');
    const cart = await cartResponse.json();
    const detail = {
      cart,
      item,
      sections: {
        'api-cart-items': itemsHTML,
        'api-cart-subtotal': subtotalHTML
      },
      rendered: false,
      error: null
    };

    drawer.dispatchEvent(new CustomEvent('keith:cart:render', { detail }));
    if (!detail.rendered) {
      throw detail.error || new Error('The cart drawer renderer is not ready.');
    }

    drawer.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: false }));
    return true;
  }

  function initMain(root) {
    if (!root || root.dataset.ready) return;
    root.dataset.ready = 'true';
    const form = root.querySelector('[data-k2cp-form]');
    const productData = root.querySelector('[data-k2cp-product-json]');
    const variants = productData ? JSON.parse(productData.textContent) : [];
    const variantId = form && form.querySelector('[name="id"]');
    const prices = all(root, '[data-k2cp-price]');
    const compare = root.querySelector('[data-k2cp-compare]');
    const submit = form && form.querySelector('[data-k2cp-submit]');
    const submitText = submit && submit.querySelector('[data-k2cp-submit-text]');
    const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    const formatMoney = (cents) => {
      const amount = (Number(cents || 0) / 100).toFixed(2);
      return moneyFormat.replace(/\{\{\s*amount(?:_with_comma_separator)?\s*\}\}/, amount);
    };
    const selectMedia = (id) => {
      if (!id) return;
      all(root, '[data-k2cp-media]').forEach((item) => item.classList.toggle('is-active', String(item.dataset.mediaId) === String(id)));
      all(root, '[data-k2cp-thumb]').forEach((item) => item.classList.toggle('is-active', String(item.dataset.mediaId) === String(id)));
      const target = root.querySelector(`[data-k2cp-media][data-media-id="${id}"]`);
      const count = root.querySelector('[data-k2cp-gallery-count]');
      const items = all(root, '[data-k2cp-media]');
      const index = items.indexOf(target);
      if (count && index > -1) count.textContent = `${index + 1} / ${items.length}`;
      if (target && window.matchMedia('(max-width: 989px)').matches) target.scrollIntoView({behavior:'smooth',block:'nearest',inline:'center'});
    };
    all(root, '[data-k2cp-thumb]').forEach((button) => button.addEventListener('click', () => selectMedia(button.dataset.mediaId)));
    const syncVariant = () => {
      const selected = all(form, '[data-k2cp-option]:checked').sort((a,b) => Number(a.dataset.position) - Number(b.dataset.position)).map((input) => input.value);
      const variant = variants.find((item) => !selected.length || item.options.every((value, index) => value === selected[index]));
      if (!variant) return;
      variantId.value = variant.id;
      prices.forEach((item) => { item.textContent = formatMoney(variant.price); });
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compare.hidden = false;
        compare.textContent = formatMoney(variant.compare_at_price);
      } else compare.hidden = true;
      submit.disabled = !variant.available;
      submitText.textContent = variant.available ? submit.dataset.availableText : submit.dataset.soldText;
      if (variant.featured_media) selectMedia(variant.featured_media.id);
      if (history.replaceState) history.replaceState({}, '', `${location.pathname}?variant=${variant.id}`);
    };
    all(form, '[data-k2cp-option]').forEach((input) => input.addEventListener('change', syncVariant));
    all(form, '[data-k2cp-qty]').forEach((button) => button.addEventListener('click', () => {
      const input = form.querySelector('[name="quantity"]');
      input.value = Math.max(1, Number(input.value || 1) + Number(button.dataset.k2cpQty));
    }));

    const modeInputs = all(form, '[data-k2cp-mode]');
    const panels = all(form, '[data-k2cp-panel]');
    const syncMode = () => {
      const checked = modeInputs.find((input) => input.checked);
      const mode = checked ? checked.value : 'Text Only';
      panels.forEach((panel) => {
        const active = panel.dataset.k2cpPanel === mode;
        panel.classList.toggle('is-active', active);
        all(panel, 'input, textarea').forEach((field) => {
          field.disabled = !active;
          field.required = active && field.dataset.required === 'true';
        });
      });
    };
    modeInputs.forEach((input) => input.addEventListener('change', syncMode));
    syncMode();

    const areaCopyNode = root.querySelector('[data-k2cp-area-copy]');
    const areaCopy = areaCopyNode ? JSON.parse(areaCopyNode.textContent) : {};
    const areaDescription = root.querySelector('[data-k2cp-area-description]');
    const syncArea = () => {
      const selected = root.querySelector('[data-k2cp-area]:checked');
      if (!selected) return;
      const key = selected.dataset.k2cpArea;
      const copy = areaCopy[key] || {};
      if (areaDescription) areaDescription.textContent = copy.description || '';
    };
    all(root, '[data-k2cp-area]').forEach((input) => input.addEventListener('change', syncArea));
    syncArea();

    form.addEventListener('submit', async (event) => {
      const error = root.querySelector('[data-k2cp-error]');
      if (!form.checkValidity()) {
        event.preventDefault();
        error.classList.add('is-visible');
        form.reportValidity();
        return;
      }

      event.preventDefault();
      error.classList.remove('is-visible');

      if (form.dataset.k2cpAdding === 'true') return;
      form.dataset.k2cpAdding = 'true';
      const originalText = submitText.textContent;
      submit.disabled = true;
      submitText.textContent = 'ADDING...';

      try {
        const formData = new FormData(form);
        formData.set('sections', 'api-cart-items,api-cart-subtotal');
        formData.set('sections_url', window.theme?.routes?.root_url || window.Shopify?.routes?.root || '/');

        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });

        if (!response.ok) {
          let message = 'Unable to add this personalized item to cart.';
          try {
            const data = await response.json();
            message = data.description || data.message || message;
          } catch (parseError) {}
          throw new Error(message);
        }

        const item = await response.json();
        await openCartDrawer(item);
        submitText.textContent = 'ADDED';
        window.setTimeout(() => {
          submitText.textContent = submit.dataset.availableText || originalText;
        }, 1100);
      } catch (addError) {
        console.warn('[KEITH Custom PDP] Add to cart failed:', addError);
        error.textContent = addError.message || 'Unable to add this personalized item to cart.';
        error.classList.add('is-visible');
        submitText.textContent = 'TRY AGAIN';
        window.setTimeout(() => {
          submitText.textContent = submit.dataset.availableText || originalText;
        }, 1600);
      } finally {
        form.dataset.k2cpAdding = 'false';
        const selectedId = String(variantId?.value || '');
        const selectedVariant = variants.find((item) => String(item.id) === selectedId);
        submit.disabled = selectedVariant ? !selectedVariant.available : false;
      }
    });

    const lightbox = root.querySelector('[data-k2cp-lightbox]');
    all(root, '[data-k2cp-zoom-src]').forEach((zoom) => zoom.addEventListener('click', () => {
      if (!lightbox) return;
      lightbox.querySelector('img').src = zoom.dataset.k2cpZoomSrc;
      lightbox.hidden = false;
    }));
    all(root, '[data-k2cp-lightbox-close]').forEach((button) => button.addEventListener('click', () => { lightbox.hidden = true; }));

    const gallery = root.querySelector('[data-k2cp-gallery]');
    if (gallery && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a,b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) selectMedia(visible.target.dataset.mediaId);
      }, {root: gallery, threshold:[.45,.7]});
      all(root, '[data-k2cp-media]').forEach((item) => observer.observe(item));
    }

    const sticky = root.querySelector('[data-k2cp-sticky]');
    if (sticky && submit && 'IntersectionObserver' in window) {
      new IntersectionObserver((entries) => sticky.classList.toggle('is-visible', !entries[0].isIntersecting), {threshold:.1}).observe(submit);
      sticky.querySelector('button').addEventListener('click', () => submit.click());
    }
    syncVariant();
  }

  function initCompare(root) {
    if (!root || root.dataset.ready) return;
    root.dataset.ready = 'true';
    const range = root.querySelector('[data-k2cp-compare-range]');
    const stage = root.querySelector('[data-k2cp-compare-stage]');
    if (!range || !stage) return;
    const update = () => stage.style.setProperty('--k2cp-pos', `${range.value}%`);
    range.addEventListener('input', update);
    update();
  }

  const init = (scope = document) => {
    installStableCartToggle();
    all(scope, '[data-k2cp-main]').forEach(initMain);
    all(scope, '[data-k2cp-compare]').forEach(initCompare);
  };

  document.documentElement.dataset.k2cpScriptVersion = '2.18';
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
